'use client';
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { userChangeIsLoggedIn } from '~/redux/features/userSlide';
import { useGlobalPopup } from '~/components/GlobalPopupProvider';
import useLogout from '~/hooks/useLogout';
import { accountNavLinks } from '~/components/elements/AccountSidebar';

const ICONS_BASE = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/static/img/icons`;
const NAV_ICON_SIZE = 16;

const SIDES = ['top', 'right', 'bottom', 'left'];
const FREEZE_PROPS = [
  'display',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'width',
  'min-width',
  'max-width',
  'z-index',
  'transform',
  'overflow',
  'box-shadow',
  'background-color',
  'background-image',
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-left-radius',
  'border-bottom-right-radius',
  ...SIDES.flatMap((s) => [
    `margin-${s}`,
    `padding-${s}`,
    `border-${s}-width`,
    `border-${s}-style`,
    `border-${s}-color`,
  ]),
];
const FORCE_PROPS = ['visibility', 'opacity', 'pointer-events', 'transition'];

const readComputedStyles = (el) => {
  const cs = window.getComputedStyle(el);
  const out = {};
  FREEZE_PROPS.forEach((p) => {
    out[p] = cs.getPropertyValue(p);
  });
  return out;
};

const applyInlineStyles = (el, styles) => {
  Object.entries(styles).forEach(([prop, value]) => {
    if (value) el.style.setProperty(prop, value, 'important');
  });
};

const clearInlineStyles = (el) => {
  FREEZE_PROPS.concat(FORCE_PROPS).forEach((p) => el.style.removeProperty(p));
};

const BellIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M12 3.5c-3 0-5 2.2-5 5.2v3.1c0 .6-.2 1.2-.6 1.7l-.9 1.1c-.6.8 0 1.9 1 1.9h11c1 0 1.6-1.1 1-1.9l-.9-1.1c-.4-.5-.6-1.1-.6-1.7V8.7c0-3-2-5.2-5-5.2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.7 19.5a2.3 2.3 0 0 0 4.6 0"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WalletIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18a1 1 0 0 1 1 1v2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 7.5V17a2 2 0 0 0 2 2h12.5a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 1 4 7.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="15.5" cy="13.5" r="1.1" fill="currentColor" />
  </svg>
);

const StarIcon = ({ size = NAV_ICON_SIZE }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width={size} height={size}>
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8L3.5 9.7l5.9-.9L12 3.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const AccountLinkIcon = ({ icon }) => {
  const iconStyle = {
    fontSize: `${NAV_ICON_SIZE}px`,
    width: `${NAV_ICON_SIZE}px`,
    height: `${NAV_ICON_SIZE}px`,
    lineHeight: '1',
    marginRight: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    flexShrink: 0,
  };
  if (icon === 'icon-bell') {
    return (
      <i className={icon} style={iconStyle}>
        <BellIcon />
      </i>
    );
  }
  if (icon === 'icon-wallet') {
    return (
      <i className={icon} style={iconStyle}>
        <WalletIcon />
      </i>
    );
  }
  if (icon === 'icon-star') {
    return (
      <i className={icon} style={iconStyle}>
        <StarIcon />
      </i>
    );
  }
  return <i className={icon} style={iconStyle} />;
};

const AccountQuickLinks = () => {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const { showPopup } = useGlobalPopup();
  const { handleLogout } = useLogout({ showPopup });

  const isLoggedInRedux = useSelector((state) => state.user.isLoggedIn);

  const [userData, setUserData] = useState(null);
  const [viewState, setViewState] = useState('checking');
  const [isHovered, setIsHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);

  const rootRef = useRef(null);
  const snapshotRef = useRef(null);

  const isOpen = isHovered || pinned;

  const closeDropdown = useCallback(() => {
    snapshotRef.current = null;
    setPinned(false);
    setIsHovered(false);
  }, []);

  const fetchUserData = useCallback(async (token, signal) => {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
            cache: 'no-store',
          });
          if (!response.ok) {
            if (attempt < 3) await sleep(350);
            continue;
          }
          const data = await response.json();
          setUserData(data);
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
          if (attempt < 3) await sleep(350);
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Fehler beim Abrufen der Benutzerdaten:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (!token) {
      dispatch(userChangeIsLoggedIn(false));
      setUserData(null);
      setViewState('guest');
      return;
    }
    setViewState('loggedIn');
    dispatch(userChangeIsLoggedIn(true));
    const controller = new AbortController();
    fetchUserData(token, controller.signal);
    return () => controller.abort();
  }, [dispatch]);

  useLayoutEffect(() => {
    const token = localStorage.getItem('jwt');
    if (token) {
      setViewState('loggedIn');
      dispatch(userChangeIsLoggedIn(true));
    } else {
      setViewState('guest');
      dispatch(userChangeIsLoggedIn(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedInRedux && viewState !== 'loggedIn') {
      setViewState('loggedIn');
      const token = localStorage.getItem('jwt');
      if (token) {
        const controller = new AbortController();
        fetchUserData(token, controller.signal);
        return () => controller.abort();
      }
      return undefined;
    }

    if (!isLoggedInRedux && viewState === 'loggedIn') {
      setUserData(null);
      closeDropdown();
      setViewState('guest');
    }
    return undefined;
  }, [isLoggedInRedux, viewState, fetchUserData, closeDropdown]);

  useEffect(() => {
    closeDropdown();
  }, [pathname, closeDropdown]);

  useEffect(() => {
    if (!pinned) return;

    const onDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      if (
        e.target.closest &&
        e.target.closest('[role="dialog"], [class*="modal"], [class*="popup"]')
      ) return;
      closeDropdown();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned, closeDropdown]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const wrapper = root.querySelector('.ps-dropdown-wrapper');
    const content = root.querySelector('.ps-block__content');
    if (!wrapper || !content) return;

    if (!pinned) {
      clearInlineStyles(wrapper);
      clearInlineStyles(content);
      return;
    }

    const snap = snapshotRef.current;
    [
      [wrapper, snap?.wrapper],
      [content, snap?.content],
    ].forEach(([el, styles]) => {
      if (styles) applyInlineStyles(el, styles);
      el.style.setProperty('transition', 'none', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('pointer-events', 'auto', 'important');
      if (!styles) el.style.setProperty('display', 'block', 'important');
    });
  }, [pinned, isOpen]);

  const takeHoverSnapshot = useCallback(() => {
    const root = rootRef.current;
    snapshotRef.current = null;
    if (!root || !root.matches(':hover')) return;
    const wrapper = root.querySelector('.ps-dropdown-wrapper');
    const content = root.querySelector('.ps-block__content');
    if (!wrapper || !content) return;
    clearInlineStyles(wrapper);
    clearInlineStyles(content);
    snapshotRef.current = {
      wrapper: readComputedStyles(wrapper),
      content: readComputedStyles(content),
    };
  }, []);

  const togglePinned = useCallback(() => {
    if (pinned) {
      snapshotRef.current = null;
      setPinned(false);
    } else {
      takeHoverSnapshot();
      setPinned(true);
    }
  }, [pinned, takeHoverSnapshot]);

  const isLinkActive = (url) =>
    Boolean(pathname) && (pathname === url || pathname.startsWith(`${url}/`));

  if (viewState === 'checking') {
    return (
      <div className="ps-block--user-account">
        <div className="ps-block__header">
          <i className="icon-user" />
          <span className="account-text account-text--checking">Mein Konto</span>
        </div>
      </div>
    );
  }

  if (viewState === 'guest') {
    return (
      <div className="ps-block--user-header">
        <div className="ps-block__left">
          <i className="icon-user" />
        </div>
        <div className="ps-block__right">
          <Link href="/account/login">Anmelden</Link>
          <Link href="/account/register">Registrieren</Link>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="ps-block--user-account hover-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="ps-block__header">
        {/* Icon: navigiert wie vorher zu /account */}
        <Link
          href="/account"
          onClick={closeDropdown}
          style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
          aria-label="Mein Konto"
        >
          <div className="ps-header-icon-wrap">
            <i className="icon-user" />
            <span className="ps-logged-in-badge">✓</span>
          </div>
        </Link>

        {/* Text: fixiert / löst das Dropdown (wie "Warenkorb" bei MiniCart) */}
        <span
          className="account-text account-text--toggle"
          role="button"
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={isOpen}
          style={{ position: 'relative', zIndex: 5, pointerEvents: 'auto' }}
          onClick={togglePinned}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              togglePinned();
            }
          }}
        >
          Mein Konto
        </span>
      </div>

      {isOpen && (
        <div className="ps-dropdown-wrapper">
          <div className="ps-block__content">
            <div className="content-inner">
              <div className="dropdown-arrow"></div>
              <div className="user-info">
                <div className="user-avatar-small">
                  <Link href="/account" className="user-avatar-link" onClick={closeDropdown}>
                    <i className="icon-user" />
                  </Link>
                </div>
                <div className="user-details">
                  <Link href="/account" className="user-name-link" onClick={closeDropdown}>
                    <span className="user-name">
                      {userData
                        ? (`${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || 'Mein Konto')
                        : 'Lade...'}
                    </span>
                  </Link>
                  {userData === null ? null : userData.customerNumber ? (
                    <span className="customer-id">Kundennummer: {userData.customerNumber}</span>
                  ) : (
                    <span className="customer-id">
                      Kundennummer:
                      <Link href="/account" className="customer-id--unconfirmed-link" onClick={closeDropdown}>
                        <strong className="customer-id--unconfirmed">Bestätigung erforderlich</strong>
                      </Link>
                    </span>
                  )}
                </div>
              </div>
              <ul className="ps-list--arrow">
                {accountNavLinks.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className={`account-link${isLinkActive(item.href) ? ' active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <AccountLinkIcon icon={item.icon} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
                <li className="ps-block__footer">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      closeDropdown();
                      handleLogout();
                    }}
                    onMouseEnter={() => setLogoutHovered(true)}
                    onMouseLeave={() => setLogoutHovered(false)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: logoutHovered ? '#555' : '#333',
                      color: 'white',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none',
                      transition: 'background-color 0.3s',
                    }}
                  >
                    <img
                      src={`${ICONS_BASE}/logout.svg`}
                      alt=""
                      width={14}
                      height={14}
                      style={{
                        marginRight: '10px',
                        width: '14px',
                        height: '14px',
                        display: 'block',
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                    Ausloggen
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .hover-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          font-size: 12px;
          position: relative;
          width: 100%;
        }
        .hover-container::after {
          content: '';
          position: absolute;
          top: 100%;
          right: 0;
          width: 100%;
          height: 12px;
          background: transparent;
          z-index: 1;
        }
        .ps-block__header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 12px;
        }
        .ps-block__header i {
          font-size: 24px;
          color: #6c757d;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .ps-header-icon-wrap {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .ps-logged-in-badge {
          position: absolute;
          bottom: -2px;
          right: -4px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #28a745;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 700;
          border: 2px solid #fff;
          line-height: 1;
          box-shadow: 0 1px 4px rgba(40, 167, 69, 0.4);
        }
        .account-text {
          margin-top: 6px;
          font-weight: 600;
          color: #333;
          font-size: 13px;
        }
        .account-text--toggle {
          cursor: pointer;
          outline: none;
        }
        .account-text--toggle:focus-visible {
          text-decoration: underline;
        }
        .ps-dropdown-wrapper {
          position: absolute;
          top: calc(100% + 11px);
          right: 0;
          width: 280px;
          z-index: 2147483647 !important;
          pointer-events: auto;
        }
        .ps-block__content {
          position: relative;
          width: 100%;
          background: #fff;
          border-radius: 0;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        }
        .content-inner {
          width: 100%;
          padding: 0;
          max-height: calc(100vh - 140px);
          overflow-y: auto;
        }
        .dropdown-arrow {
          position: absolute;
          top: -6px;
          right: 20px;
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #fff;
          filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.1));
        }
        .user-info {
          display: flex;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          background: linear-gradient(135deg, #e9ecef 100%, #e9ecef 100%);
        }
        .user-avatar-small {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
        }
        .user-avatar-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          text-decoration: none;
        }
        .user-avatar-small i {
          color: #6c757d;
          font-size: 16px;
        }
        .user-details {
          flex: 1;
          min-width: 0;
        }
        .user-name-link {
          display: block;
          text-decoration: none;
        }
        .user-name-link:hover .user-name {
          text-decoration: underline;
        }
        .user-name {
          display: block;
          font-weight: 600;
          color: #2c3e50;
          font-size: 14px;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .customer-id {
          display: flex;
          flex-direction: column;
          font-size: 12px;
          color: #7f8c8d;
          font-weight: 500;
        }
        .customer-id--unconfirmed-link {
          text-decoration: none;
        }
        .customer-id--unconfirmed-link:hover .customer-id--unconfirmed {
          text-decoration: underline;
        }
        .customer-id--unconfirmed {
          display: block;
          color: #bf0000;
          font-weight: 600;
          font-size: 11px;
          margin-top: 2px;
        }
        .ps-list--arrow {
          list-style: none;
          padding: 8px 0;
          margin: 0;
        }
        .ps-list--arrow li {
          margin: 0;
        }
        .account-link {
          display: flex;
          align-items: center;
          padding: 11px 20px;
          color: #2c3e50;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .account-link:hover {
          background-color: #f8f9fa;
          color: #1a73e8;
        }
        .account-link span {
          flex: 1;
        }
        .account-link.active,
        .account-link.active:hover {
          background-color: #e3f2fd;
          color: #1a73e8;
          font-weight: 600;
        }
        /* Icons werden in der Unterkomponente AccountLinkIcon gerendert,
           daher :global, damit styled-jsx die Selektoren nicht wegscoped. */
        :global(.account-link i) {
          color: #6c757d;
        }
        :global(.account-link:hover i) {
          color: #1a73e8;
        }
        :global(.account-link.active i) {
          color: #1a73e8;
        }
        :global(.account-link i svg) {
          color: inherit;
        }
        .ps-block__footer {
          border-top: 1px solid rgba(0, 0, 0, 0.08);
          padding: 8px 12px 0;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default AccountQuickLinks;